import React, { useEffect, useState, useMemo, memo, useRef } from "react";
import {
  AppState,
  FlatList,
  InteractionManager,
  StyleSheet,
  TextInput,
  View,
} from "react-native";
import Text from "@components/CustomText";
import Image from "@components/common/Image";
import { width, height } from "@constants/metrics";
import { TouchableOpacity } from "react-native-gesture-handler";
import { colors } from "@utils/styles";
import WhatsappDefault from "@assets/svg/WhatsappDefault";
import SearchIcon from "@assets/svg/SearchIcon";
import color from "@constants/color";
import simpleContacts from "react-native-allcontacts";
import {
  answerEarlyJourneyQuestionaire,
  getAllTeamLeaderUsers,
} from "@services/api";
import BackArrow from "@assets/svg/BackArrow";
import useGetCXAppLink from "@utils/useGetCXAppLink";
import { useDispatch, useSelector } from "react-redux";
import { InvokeType, shareOnWhatsapp } from "@utils/shareEntity";
import {
  check,
  openSettings,
  PERMISSIONS,
  request,
  RESULTS,
} from "react-native-permissions";
import analytics from "@services/analytics";
import {
  SHARING_EVENTS,
  ORDER_FLOW_EVENTS,
  EARLY_JOURNEY,
} from "@constants/analyticsEventsCT";
import Tick from "@assets/svg/Tick";
import { getData, setData } from "@lib/storage";
import { differenceInMinutes, differenceInDays } from "date-fns";
import FetchCMSEntity from "@components/CMS/FetchCMSEntity";
import { scale } from "@utils/scale";
import Button from "@components/Button";
import { actions } from "@reducers/authReducer";
import ContactSyncIcon from "@assets/svg/Jrny/ContactSyncIcon";
import SearchBar from "@components/SearchBar";
import ArrowRightMedium from "@assets/svg/ArrowRightMedium";
import { useFocusEffect } from "@react-navigation/native";
import useFocusAndAppState from "@hooks/useFocusAndAppState";

const CMS_KEY = "EJ_CMS_3";

const reStructureContactData = (data, invitedContacts, alreadyCustomers) => {
  return data
    .map((obj) => ({
      ...obj,
      number: obj.number.replaceAll(" ", "").replaceAll("-", ""),
    }))
    .map((obj) => ({
      ...obj,
      number: obj.number.startsWith("+91") ? obj.number.slice(3) : obj.number,
    }))
    .map((obj) => ({
      ...obj,
      number: obj.number.startsWith("0") ? obj.number.slice(1) : obj.number,
    }))
    .map((obj) => ({ ...obj, isInvited: invitedContacts.includes(obj.number) }))
    .filter((obj) => obj.number.match("^[1-9][0-9]{9}$"))
    .filter(
      (obj) =>
        !alreadyCustomers.find(
          (alreadyCustomer) => alreadyCustomer.number === obj.number,
        ),
    );
};

const InviteFriendsCard = memo(
  ({ name, handleOnPressSendInvite, isInvited, isAlreadyCustomer, number }) => {
    return (
      <View style={styles.contactCardContainer}>
        <View style={styles.nameContainer}>
          <Text
            numberOfLines={1}
            style={styles.nameStyle}
            newfont
            variant="body_text_2"
          >
            {name}
          </Text>
          <Text style={styles.numberText} newfont variant="body_text_3">
            {number}
          </Text>
        </View>
        {isAlreadyCustomer ? (
          <TouchableOpacity
            disabled
            style={[styles.cardCtaButton, { backgroundColor: "transparent" }]}
          >
            <View style={styles.greenCircle}>
              <Tick width={16} height={16} strokeColor="#fff" />
            </View>
            <Text
              newfont
              variant="body_text_2"
              style={{ color: colors.green1, marginLeft: 5 }}
            >
              Added
            </Text>
          </TouchableOpacity>
        ) : (
          <>
            {!isInvited && (
              <TouchableOpacity
                style={styles.cardCtaButton}
                onPress={handleOnPressSendInvite}
              >
                <WhatsappDefault size={20} />
                <Text newfont variant="body_text_2" style={styles.cardText}>
                  Invite
                </Text>
              </TouchableOpacity>
            )}
            {isInvited && (
              <TouchableOpacity
                disabled
                style={[
                  styles.cardCtaButton,
                  { backgroundColor: "transparent" },
                ]}
              >
                <Text
                  newfont
                  variant="body_text_2"
                  style={{ color: colors.green1 }}
                >
                  Invited
                </Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </View>
    );
  },
);

const InviteFriends = ({ navigation }) => {
  const [hasContactPermission, setHasContactPermission] = useState();
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState();
  const [loading, setLoading] = useState();
  const dispatch = useDispatch();
  const invitedCount = useMemo(
    () => allUsers.filter((item) => item.isInvited).length,
    [allUsers],
  );

  const appState = useRef(AppState.currentState);
  const contactPermissionAskedOnce = useRef();

  const businessCard = useSelector(
    ({ configReducer: { businessCard } }) => businessCard,
  );
  const {
    INVITE_FRIENDS_IMAGE,
    INVITE_FRIENDS_PERMISSION_DENIED,
    INVITE_FRIENDS_IMAGE_HEIGHT,
    INVITE_FRIENDS_IMAGE_WIDTH,
    INVITE_FRIENDS_EXPIRES_IN_DAY = 7,
    cmsPoweredEntities = {},
  } = useSelector(({ configReducer }) => configReducer.appSettings) || {};

  const message = useGetCXAppLink();

  useFocusAndAppState(checkPermission, checkPermission);

  useEffect(() => {
    console.log("running useEffect permission", hasContactPermission);
    if (hasContactPermission) {
      getAllContactsDataAndMerge();
      analytics.trackEvent(ORDER_FLOW_EVENTS.CONTACT_PERMISSION_GRANTED);
    } else if (hasContactPermission === false) {
      setLoading(false);
      console.log("setLoading(false)", 1);
      analytics.trackEvent(ORDER_FLOW_EVENTS.CONTACT_PERMISSION_REJECTED);
    }
  }, [hasContactPermission]);

  const getAllContactsDataAndMerge = async () => {
    try {
      setLoading(true);
      let { data: alreadyCustomers } = await getAllTeamLeaderUsers();
      alreadyCustomers = alreadyCustomers.map((customer) => ({
        key: customer.user_id,
        name: customer.user_name,
        number: customer.user_phone,
        isAlreadyCustomer: true,
      }));

      const { reStructuredUsers: oldContactData = [], lastSyncedDate } =
        (await getData("INVITE_FRIENDS_CONTACTS_DATA")) || {};
      const invitedContacts = (await getData("INVITED_FRIENDS")) ?? [];

      let users = [];

      if (
        oldContactData &&
        oldContactData.length > 0 &&
        differenceInDays(new Date(), new Date(lastSyncedDate)) <
          INVITE_FRIENDS_EXPIRES_IN_DAY
      ) {
        users = oldContactData;
      } else {
        const jsonUsers = await simpleContacts.getContacts();
        users = JSON.parse(jsonUsers);
        await setData("INVITE_FRIENDS_CONTACTS_DATA", {
          reStructuredUsers: reStructureContactData(
            users,
            invitedContacts,
            alreadyCustomers,
          ),
          lastSyncedDate: new Date().toISOString(),
        });
      }

      const reStructuredUsers = [
        ...reStructureContactData(users, invitedContacts, alreadyCustomers),
        ...alreadyCustomers,
      ];
      sortInvitedUsers(reStructuredUsers);
    } catch (err) {
      console.log(err);
      setLoading(false);
      console.log("setLoading(false)", 2);
    }
  };

  const sortInvitedUsers = (reStructuredUsers) => {
    let invitedContact = [],
      withoutInvitedContact = [];
    reStructuredUsers.forEach((item) =>
      item.isInvited
        ? invitedContact.push(item)
        : withoutInvitedContact.push(item),
    );
    invitedContact = invitedContact.sort((a, b) =>
      a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
    );
    withoutInvitedContact = withoutInvitedContact.sort((a, b) =>
      a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
    );
    setAllUsers([...invitedContact, ...withoutInvitedContact]);
  };

  useEffect(() => {
    if (allUsers && allUsers.length > 0) {
      setLoading(false);
      console.log("setLoading(false)", 3);
    }
  }, [allUsers]);

  const checkPermission = async () => {
    const permissionType = await check(PERMISSIONS.ANDROID.READ_CONTACTS);

    let permissionStatus = permissionType;
    if (
      (permissionStatus === "NOT_REQUESTED" ||
        permissionStatus === RESULTS.DENIED) &&
      !contactPermissionAskedOnce.current
    ) {
      permissionStatus = await request(PERMISSIONS.ANDROID.READ_CONTACTS);
      contactPermissionAskedOnce.current = true;
    }

    if (permissionStatus === RESULTS.GRANTED) {
      setHasContactPermission(true);
    } else {
      setHasContactPermission(false);
    }
  };

  const handleOnPressSendInvite = async ({ number, name, key }) => {
    if (businessCard?.data?.transparent_url) {
      const bCardUrl = businessCard.data.transparent_url;
      if (message && bCardUrl) {
        console.log({
          message,
          image: bCardUrl,
          invoke: InvokeType.whatsapp,
          phoneNumber: number,
        });
        shareOnWhatsapp({
          message,
          image: bCardUrl,
          invoke: InvokeType.whatsapp,
          phoneNumber: number,
        });
        setTimeout(() => {
          const itemIndex = allUsers.findIndex((item) => item.key === key);
          const mutableCurrentData = [...allUsers];
          mutableCurrentData[itemIndex].isInvited = true;
          sortInvitedUsers(mutableCurrentData);
        }, 1000);
        const invitedContacts = (await getData("INVITED_FRIENDS")) ?? [];

        if (!invitedContacts.includes(number)) {
          invitedContacts.push(number);
          await setData("INVITED_FRIENDS", invitedContacts);
        }

        analytics.trackEvent(SHARING_EVENTS.SHOP_SHARE, {
          phone_number: number,
          contact_name: name,
          screen_name: "Contact sync page",
        });
      }
    }
  };

  const handleOnPressNoPermissionCta = () => {
    openSettings().catch((err) => console.log(err));
  };

  const handlePressCta = () => {
    answerEarlyJourneyQuestionaire({ key: "cms_2_viewed", value: true });
    dispatch(actions.disableEarlyJourneyWhenComplete());
    analytics.trackEvent(EARLY_JOURNEY.CONTINUE_BUTTON_CLICKED, {
      screen: "EJ_CMS_4",
    });
  };

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <View
          style={{
            alignItems: "center",
            flexDirection: "row",
            height: 56,
            paddingHorizontal: 16,
          }}
        >
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <BackArrow />
          </TouchableOpacity>
          <Text newfont variant="entityHeading" style={styles.inviteText}>
            Sync Contacts
          </Text>
        </View>
        <View
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          <ContactSyncIcon />
          <Text newfont variant="subtitle_16" style={{ marginTop: 20 }}>
            Kripya thoda dherya rakhein
          </Text>
          <Text
            newfont
            variant="body_text_3"
            style={{ color: color.lightBlack }}
          >
            Aapki contact list sync ho rhi hai.
          </Text>
        </View>
      </View>
    );
  }

  if (!hasContactPermission && hasContactPermission !== undefined) {
    return (
      <View style={[styles.inviteContainer, { backgroundColor: "#E1F5FE" }]}>
        {INVITE_FRIENDS_PERMISSION_DENIED && (
          <Image
            resizeMode="contain"
            source={{
              uri: INVITE_FRIENDS_PERMISSION_DENIED,
            }}
            style={{ width, height: "100%" }}
          />
        )}
        <View style={styles.noPermissionCtaContainer}>
          <TouchableOpacity
            style={styles.noPermissionCta}
            onPress={handleOnPressNoPermissionCta}
          >
            <Text
              newfont
              variant="title_18_semi"
              style={{
                color: "white",
              }}
            >
              Contacts ko invite kare
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.inviteContainer}>
      <View style={styles.headerContainer}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <BackArrow />
        </TouchableOpacity>
        <Text newfont variant="entityHeading" style={styles.inviteText}>
          Invite Customers
        </Text>
      </View>
      <View style={styles.bannerContainer}>
        <FetchCMSEntity
          trackEvent={() => {}}
          id={cmsPoweredEntities[CMS_KEY]}
          type="BANNER_CAROUSEL"
          isPaddingVertical={false}
        />
      </View>
      <SearchBar
        clearSearch
        customStyles={styles.searchContainer}
        query={searchTerm}
        placeholder="Customers search karein..."
        shouldShowIcon
        handleQueryChange={(value) => setSearchTerm(value)}
        onPressRightIcon={() => setSearchTerm("")}
      />
      <View style={styles.totalCountContainer}>
        <Text newfont variant="body_text_4" style={styles.skipText}>
          {"Total Contacts : "}
        </Text>
        <Text newfont variant="entityHeading" style={styles.countText}>
          {allUsers.length}
        </Text>
      </View>
      <FlatList
        data={
          searchTerm && searchTerm.toString().length > 0
            ? allUsers.filter(
                (customer) =>
                  customer?.name
                    .toString()
                    .toLowerCase()
                    .includes(searchTerm.toString().toLowerCase()) ||
                  customer?.number
                    .toString()
                    .includes(searchTerm.toString().toLowerCase()),
              )
            : allUsers
        }
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        renderItem={({ item }) => (
          <InviteFriendsCard
            handleOnPressSendInvite={() => handleOnPressSendInvite(item)}
            name={item.name}
            isAlreadyCustomer={item.isAlreadyCustomer}
            isInvited={item.isInvited}
            number={item.number}
          />
        )}
        keyExtractor={(item) => item.key}
        windowSize={100}
      />
      <View style={styles.ctaContainer}>
        <View style={styles.invitedContainer}>
          <Text
            newfont
            variant="entityHeading"
            style={[styles.countText, { color: color.plain_green }]}
          >
            {invitedCount}
          </Text>
          <Text
            newfont
            variant="body_text_4"
            style={[styles.skipText, { color: color.mediumBlack }]}
          >
            {" invited"}
          </Text>
        </View>
        <Button
          buttonWidth="100%"
          btnText="Aage Badhen"
          buttonFontType="head1"
          height={48}
          borderRadius={10}
          onPress={handlePressCta}
          IconRight={
            <View style={styles.ctaIcon}>
              <ArrowRightMedium width={14} height={11} />
            </View>
          }
        />
      </View>
    </View>
  );
};

export default InviteFriends;

const styles = StyleSheet.create({
  inviteContainer: {
    flex: 1,
    backgroundColor: "#fff",
  },
  loaderContainer: {
    flex: 1,
    backgroundColor: "#fff",
  },
  searchContainer: {
    padding: 15,
  },
  contactCardContainer: {
    padding: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: color.lineColor,
  },
  cardCtaButton: {
    flexDirection: "row",
    width: 80,
    backgroundColor: colors.green1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
  },
  cardText: {
    color: "#fff",
    marginLeft: 5,
  },
  noPermissionCtaContainer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
  },
  noPermissionCta: {
    backgroundColor: colors.raddishPink1,
    paddingVertical: 12,
    margin: 12,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
  },
  greenCircle: {
    backgroundColor: colors.green1,
    justifyContent: "center",
    alignItems: "center",
    height: 18,
    width: 18,
    borderRadius: 18,
  },
  headerContainer: {
    height: 56,
    paddingHorizontal: 16,
    elevation: 2,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
    elevation: 8,
    shadowColor: color.mediumBlack,
    backgroundColor: colors.white,
    flexDirection: "row",
    alignItems: "center",
  },
  inviteText: {
    marginStart: 25,
    marginBottom: 0,
  },
  skipText: {
    color: color.lightBlack,
  },
  bannerContainer: {
    maxHeight: scale(180),
    width: "100%",
  },
  totalCountContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    alignItems: "center",
  },
  countText: {
    marginBottom: 0,
    color: color.mediumBlack,
    lineHeight: 24,
    paddingHorizontal: 0,
  },
  ctaContainer: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    padding: 15,
    paddingTop: 6,
    backgroundColor: "#fff",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  invitedContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  nameContainer: {
    flexDirection: "column",
  },
  nameStyle: {
    maxWidth: scale(250),
  },
  numberText: {
    color: color.lightBlack,
  },
  ctaIcon: {
    marginLeft: 10,
    marginTop: 5,
  },
});
